<script>
    import Select from "svelte-select/Select.svelte";

    import {
        CardLayout,
        ErrorModal,
        Loader,
        NotificationModal,
        SvelteTable,
        TableElementEditableStatus,
    } from "../../../Components/Common/index.svelte";

    import { apiMethods } from "../../../Services/index.svelte";

    import {
        getText,
        getTitle,
        isObjectEmpty,
        showError,
        showException,
    } from "../../../Utils/utilFunctions.svelte";
    import { addIcon } from "../../../Utils/utilConstants.svelte";

    export let groupId = Number(0);

    $: if (groupId) getDoctorsUnderGroup();

    $: selectedDoctorName = isObjectEmpty(selectedDoctor)
        ? ""
        : _getFullName(selectedDoctor) + ` (ID: ${selectedDoctor.id})`;

    let isGettingDoctorsUnderGroup = false,
        returnDoctorsArr = [],
        selectedDoctor = {},
        selectedNewDoctor = {};

    let isAddDoctorModalOpen = false,
        isConfirmAddDoctorModalOpen = false,
        isSearchingDoctors = false,
        isAddingDoctorToGroup = false,
        isDeleteDoctorModalOpen = false,
        isDeletingDoctorFromGroup = false;

    const btnOptions = [
        {
            iconName: "addIcon",
            iFont: addIcon,
            iconFunction: () => _openAddDoctorModal(),
            hasTooltip: false,
            btnText: "Add",
        },
    ];

    const columns = [
        {
            key: "id",
            title: "Doctor ID",
            value: (v) => getText(v.id),
            headerClass:
                "text-left pl-4 text-[14px] min-w-[100px] rounded-tl-xl",
            class: "text-left pl-4 text-[14px] cursor-pointer",
        },
        {
            key: "name",
            title: "Name",
            value: (v) => _getFullName(v),
            headerClass: "text-left pl-4 text-[14px]",
            class: "text-left pl-4 text-[14px] cursor-pointer",
        },
        {
            key: "preferredName",
            title: "Preferred Name",
            value: (v) => (v.preferredName ? getText(v.preferredName) : "-"),
            headerClass: "text-left pl-4 text-[14px] cursor-default",
            class: "text-left pl-4 text-[14px] cursor-pointer",
        },
        {
            key: "phone",
            title: "Phone No.",
            value: (v) => v.phone,
            headerClass: "text-left pl-4 text-[14px] cursor-default",
            class: "text-left pl-4 text-[14px] cursor-pointer",
        },
        {
            key: "providersNumber",
            title: "Providers Number(s)",
            renderValue: (v) =>
                _renderListComponent(v.providers, "providersNumber"),
            headerClass: "text-left pl-4 text-[14px] cursor-default",
            class: "text-left pl-4 text-[14px] cursor-pointer",
        },
        {
            key: "users",
            title: "Users",
            renderValue: (v) => _renderListComponent(v.users, "users"),
            headerClass: "text-left pl-4 text-[14px]",
            class: "text-left pl-4 text-[14px] cursor-pointer",
        },
        {
            key: "status",
            title: "Status",
            headerClass: "text-left px-3 cursor-default",
            class: "text-left px-3 w-[10%]",
            renderComponent: {
                component: TableElementEditableStatus,
                props: {
                    type: "doctor",
                    onFinish: () => getDoctorsUnderGroup(),
                },
            },
        },
        {
            key: "actions",
            title: "Actions",
            headerClass:
                "text-left px-3 cursor-default rounded-tr-xl w-[2%]",
            class: "text-left px-3 cursor-pointer",
            renderValue: (v) => _renderActionIcons(),
        },
    ];

    const getDoctorsUnderGroup = async () => {
        isGettingDoctorsUnderGroup = true;

        await apiMethods.getMethod
            .getAccount(0, 999999, {
                "filter[groupID]": groupId,
            })
            .then((res) => _handleDoctorsUnderGroup(res))
            .catch((err) => showException(err));

        isGettingDoctorsUnderGroup = false;
    };

    const addDoctorToGroup = async () => {
        isAddingDoctorToGroup = true;

        await apiMethods.putMethod
            .updateDoctor({ ...selectedDoctor, groupId })
            .then((res) => _handleAfterAddDoctor(res))
            .catch((err) => showException(err));

        getDoctorsUnderGroup();

        _closeConfirmAddDoctorModal();
        _closeAddDoctorModal();

        isAddingDoctorToGroup = false;
    };

    const deleteDoctorFromGroup = async () => {
        isDeletingDoctorFromGroup = true;

        await apiMethods.putMethod
            .updateDoctor({ ...selectedDoctor, groupId: Number(0) })
            .then((res) => _handleAfterDeleteDoctor(res))
            .catch((err) => showException(err));

        getDoctorsUnderGroup();

        _closeDeleteModal();

        isDeletingDoctorFromGroup = false;
    };

    const loadDoctors = async (searchText) => {
        if (!searchText) return [];

        isSearchingDoctors = true;

        const res = await apiMethods.getMethod.getAccount(0, 999999, {
            search: searchText,
        });

        if (!res?.results.length) return [];

        const result = [];

        res.results.map((doctor) => {
            result.push({
                ...doctor,
                displayName: getDoctorNameLabel(doctor),
            });
        });

        isSearchingDoctors = false;

        return result;
    };

    const getDoctorNameLabel = (option) =>
        _getFullName(option) + ` (ID: ${option.id})`;

    const handleSelectDoctor = () => {
        selectedDoctor = { ...selectedNewDoctor.doctor };
    };

    const clearSelectedDoctor = () => {
        selectedNewDoctor = {};
        selectedDoctor = {};
    };

    const handleClickCell = ({ target: { className } }, rowData, key) => {
        if (key == "actions") {
            if (className.includes("deleteIcon")) _openDeleteModal(rowData);
        }
    };

    const handleClickCol = ({ target: { className } }, colData, key) => {};

    // HELPER FUNCTIONS
    const _handleDoctorsUnderGroup = (res) => {
        if (!res || res.error || !res.results)
            showError(
                "Failed to load doctors under this group, please refresh the page and try again and contact support if the issue persists.",
            );
        else returnDoctorsArr = [...res.results];
    };

    const _handleAfterAddDoctor = (res) => {
        if (!res || res.error || !res.id)
            showError(
                "Failed to add selected doctor to this group, please refresh the page and try again and contact support if the issue persists.",
            );
    };

    const _handleAfterDeleteDoctor = (res) => {
        if (!res || res.error || !res.id)
            showError(
                "Failed to delete selected doctor from this group, please refresh the page and try again and contact support if the issue persists.",
            );
    };

    const _getFullName = (v) => {
        return `${getTitle(v.title)} ${v.firstName}${
            v.middleName ? " " + v.middleName : ""
        }${v.lastName ? " " + v.lastName : ""}`;
    };

    const _renderListComponent = (arr, str) => {
        const result = [];
        let component;
        if (arr && arr.length) {
            arr.forEach((target) => {
                switch (str) {
                    case "providersNumber":
                        component =
                            component = `<div class="showProvider${target.id}">${target.number} (${target.location})</div>`;
                        break;
                    case "users":
                        component = `<div class="showProvider${target.id}">${target.fullName} (${target.email})</div>`;
                        break;
                    default:
                        break;
                }
                result.push(component);
            });
        }
        return result.toString().replaceAll(",", "<br>");
    };

    const _renderActionIcons = () => {
        return `<div class="flex flex-1 items-center justify-start gap-x-2">
                    <div class="deleteIcon tooltip tooltip-secondary tooltip-left flex items-center justify-center w-[30px] h-[30px] hover:rounded-full hover:bg-secondary hover:text-white" data-tip="Remove Doctor">
                        <i class="deleteIcon fa-solid fa-trash"></i>
                    </div>
                </div>`;
    };

    const _openAddDoctorModal = () => (isAddDoctorModalOpen = true);

    const _closeAddDoctorModal = () => {
        isAddDoctorModalOpen = false;
        selectedDoctor = {};
        selectedNewDoctor = {};
    };

    const _openConfirmAddDoctorModal = () =>
        (isConfirmAddDoctorModalOpen = true);

    const _closeConfirmAddDoctorModal = () =>
        (isConfirmAddDoctorModalOpen = false);

    const _openDeleteModal = (obj) => {
        selectedDoctor = { ...obj };
        isDeleteDoctorModalOpen = true;
    };

    const _closeDeleteModal = () => {
        selectedDoctor = {};
        isDeleteDoctorModalOpen = false;
    };
</script>

<CardLayout title="Doctor Under Group" {btnOptions}>
    {#if isGettingDoctorsUnderGroup}
        <div class="flex gap-x-2 p-4 items-center">
            <p class="font-bold">Loading doctors...</p>
            <Loader />
        </div>
    {:else if returnDoctorsArr.length}
        <SvelteTable
            {columns}
            rows={returnDoctorsArr}
            clickCellFunc={({ detail: { event, row, key } }) => {
                handleClickCell(event, row, key);
            }}
            clickColFunc={({ detail: { event, row, key } }) => {
                handleClickCol(event, row, key);
            }}
        />
    {:else}
        <div class="grid flex-1 h-full card bg-white rounded-box">
            <div
                class="flex flex-1 items-center justify-between px-2 py-4 w-full lg:flex-row"
            >
                <p>No doctors currently under this group.</p>
            </div>
        </div>
    {/if}
</CardLayout>

<NotificationModal
    isModalOpen={isAddDoctorModalOpen}
    title="Select doctor to add to group"
    submitText="Add"
    cancelText="Cancel"
    contentClass="p-6 flex flex-col"
    closeModal={_closeAddDoctorModal}
    confirmAction={_openConfirmAddDoctorModal}
    closeModalFunction={_closeAddDoctorModal}
>
    <div class="flex gap-x-4 w-full items-center">
        <p class="w-48">Select Doctor:</p>
        <div class="flex items-center w-full">
            <Select
                bind:value={selectedNewDoctor.doctor}
                class="flex flex-1 max-h-[35px] rounded-full"
                loadOptions={loadDoctors}
                itemId="id"
                label="displayName"
                on:clear={clearSelectedDoctor}
                on:select={handleSelectDoctor}
                noOptionsMessage="No Doctors Found"
                disabled={isAddingDoctorToGroup}
            />
        </div>
    </div>
</NotificationModal>

<NotificationModal
    isModalOpen={isConfirmAddDoctorModalOpen}
    title="Delete"
    submitText={isAddingDoctorToGroup ? "Adding" : "Confirm"}
    closeModal={_closeConfirmAddDoctorModal}
    confirmAction={addDoctorToGroup}
    closeModalFunction={_closeConfirmAddDoctorModal}
>
    Add doctor {selectedDoctorName} to this group?
</NotificationModal>

<NotificationModal
    isModalOpen={isDeleteDoctorModalOpen}
    submitText="Cancel"
    title="Remove Doctor"
    isReverseBtns={true}
    cancelText={isDeletingDoctorFromGroup ? "Removing" : "Remove"}
    closeModal={deleteDoctorFromGroup}
    confirmAction={_closeDeleteModal}
    isConfirming={isDeletingDoctorFromGroup}
    closeModalFunction={_closeDeleteModal}
>
    Remove doctor {selectedDoctorName} from this group?
</NotificationModal>
